
<script setup lang="ts">
import { computed, ref } from 'vue';
import Dropdown from './Dropdown.vue';
import { Identifier } from 'deepslate';
import ListDropdownGroup from './ListDropdownGroup.vue';
import { useSettingsStore } from '../../stores/useSettingsStore';
import { useI18n } from 'vue-i18n';

const props = defineProps({
        type: String,
        placeholder: String,
        tags: Object,
        entries: Object,
        selected: Object,
        icons: Function,
        colors: Function,
        embedded: Boolean
})

const i18n = useI18n()
const settingsStore = useSettingsStore()

defineEmits(['toggle', 'disableGroup', 'clear'])

const search = ref("")

function normalizeSearch(value: string){
    return value.trim().toLocaleLowerCase(i18n.locale.value.replace("_", "-")).replace(/[\s_:\/-]+/g, "")
}

const localized_entries = computed(() => (props.entries as Identifier[]).map(e => {
    return {id: e, localized: settingsStore.getLocalizedName(props.type!, e, true)}
}))

const grouped_entries = computed<{[key: string]: {id: Identifier, localized: string}[]}>(() => {
    const normalizedSearch = normalizeSearch(search.value)
    return localized_entries.value.filter(b => normalizeSearch(b.localized).includes(normalizedSearch) || normalizeSearch(b.id.toString()).includes(normalizedSearch)).sort().reduce((groups, entry) => {
        groups[entry.id.namespace] = groups[entry.id.namespace] ?? []
        groups[entry.id.namespace].push(entry)
        return groups
    }, {} as {[key: string]: {id: Identifier, localized: string}[]})
})

</script>


<template>
    <Dropdown class="dropdown" :embedded="embedded">
        <div class="search-row">
            <input id="search" :placeholder="props.placeholder" spellcheck="false" v-model="search" />
            <button v-if="selected && selected.size > 0" class="clear" type="button" @click="$emit('clear')">
                {{ i18n.t('ui.clear') }}
            </button>
        </div>
        <div class="entry_list">
            <ListDropdownGroup
                v-for="group in Object.entries(grouped_entries)"
                :type="type"
                :entries="group[1]"
                :group_name="group[0]"
                :selected="selected"
                :icons="icons"
                :colors="colors"
                @toggle="(entry: Identifier) => $emit('toggle', entry)" 
                @disableGroup="() => $emit('disableGroup', group[0])"
                />
        </div>
    </Dropdown>
</template>

<style scoped>
    .dropdown {
        padding: 0;
        background-color: #ffffff;
    }

    .dropdown.embedded {
        border: 0;
        box-shadow: none;
        background-color: transparent;
    }

    .search-row {
        display: flex;
        width: 100%;
        gap: 0.5rem;
        padding: 0.5rem 0.65rem;
        box-sizing: border-box;
        background-color: #ffffff;
        border-bottom: 1px solid #e2e8f0;
    }

    input {
        background-color: #f8fafc;
        color: #0f172a;
        padding: 0.55rem 0.7rem;
        width: 0;
        flex-grow: 1;
        box-sizing: border-box;
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        outline: none;
    }

    input:hover {
        border-color: #94a3b8;
    }

    input:focus {
        border-color: #4d8c61;
        box-shadow: 0 0 0 3px rgba(77, 140, 97, 0.14);
    }

    .clear {
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        background-color: #ffffff;
        color: #475569;
        padding: 0 0.75rem;
        font-weight: 600;
        cursor: pointer;
    }

    .clear:hover {
        background-color: #f1f5f9;
        border-color: #94a3b8;
    }

    .entry_list {
        height: 22rem;
        width: 100%;
        padding: 0;
        background-color: #ffffff;
        overflow-y: scroll;
        list-style-type: none;
        overflow-x: visible;
        border-radius: 0 0 8px 8px;
    }

    .dropdown.embedded .entry_list {
        height: min(24rem, 38vh);
    }

    .entry_list::-webkit-scrollbar {
        width: 10px;
    }

    .entry_list::-webkit-scrollbar-thumb {
        background-color: #cbd5e1;
        border-radius: 999px;
        border: 3px solid #ffffff;
    }


</style>
